<template>
  <div
    class="zm-time-input"
    :class="{'inner-focus': isFocus, 'zm-time-input--large': inputSize === 'large'}"
    @keydown.stop="onKeyDown"
    :style="elStyl"
    role="group"
    aria-label="Select Time, Use left and right arrows to switch between hours, minutes, and seconds"
  >
    <slot name="prefix"></slot>
    <!--<i class="zm-icon-time-outline"></i>-->
    <simple-input-number
      ref="hours"
      :value="hours"
      :ranges="timeList['hours']"
      @input="changeHour"
      @focus.native="handleFocus(0)"
      @blur.native="handleBlur"
      :aria-label="'hour:'+hours"
    >
    </simple-input-number>
    <span class="zm-time-input__sep">:</span>
    <simple-input-number
      ref="minutes"
      :value="minutes"
      :ranges="timeList['minutes']"
      @input="changeMinute"
      @focus.native="handleFocus(1)"
      @blur.native="handleBlur"
      :aria-label="'minute:'+minutes"
    />
    <span class="zm-time-input__sep" v-if="showSeconds">:</span>
    <simple-input-number
      v-if="showSeconds"
      ref="seconds"
      :value="seconds"
      :ranges="timeList['seconds']"
      @input="changeSecond"
      @focus.native="handleFocus(2)"
      @blur.native="handleBlur"
      :aria-label="'second:'+seconds"
    />
  </div>
</template>

<script>
  import { modifyTime } from '../util';
  import AriaUtils from '../../../utils/aria-utils';
  import SimpleInputNumber from './simple-input-number';
  import TimePickerMixin from '../util/time-picker-mixin';

  let _uid = 0;

  function uid() {
    return _uid++;
  }

  export default {
    name: 'time-input',
    mixins: [TimePickerMixin],
    components: {
      SimpleInputNumber
    },
    inject: {
      ZmForm: {
        default: ''
      },
      ZmFormItem: {
        default: ''
      }
    },
    props: {
      value: {
        type: Date,
        default: new Date()
      },
      format: String,
      selectableRange: {
        type: String | Array,
        default() {
          return [];
        }
      },
      showSeconds: {
        type: Boolean,
        default: true
      },
      size: String
    },
    data() {
      return {
        focusIndex: 0,
        focusRefs: ['hours', 'minutes', 'seconds'],
        isFocus: false
      };
    },
    computed: {
      id() {
        return 'time-picker-' + uid();
      },
      _elFormItemSize() {
        return (this.ZmFormItem || {}).elFormItemSize;
      },
      inputSize() {
        return this._elFormItemSize || (this.$ELEMENT || {}).size || this.size;
      }
    },
    methods: {
      onKeyDown(e) {
        const keyCode = e.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.right:
            if (this.focusIndex < 2) {
              this.focusIndex++;
            }
            e.stopPropagation();
            e.preventDefault();
            break;
          case keys.left:
            if (this.focusIndex > 0) {
              this.focusIndex--;
            }
            e.stopPropagation();
            e.preventDefault();
            break;
          case keys.tab:
            this.$emit('tab');
            break;
          case keys.esc:
            this.$emit('esc', e);
            break;
          case keys.enter:
            this.$emit('enter', e);
            break;
        }
      },
      focusEl(el) {
        if (!el) {
          return;
        }
        el.focus();
      },
      changeHour(value) {
        this.modifyDateField('hours', value);
      },
      changeMinute(value) {
        this.modifyDateField('minutes', value);
      },
      changeSecond(value) {
        this.modifyDateField('seconds', value);
      },
      handleFocus(index) {
        this.isFocus = true;
        this.focusIndex = index;
      },
      handleBlur() {
        this.isFocus = false;
      },
      modifyDateField(type, value) {
        switch (type) {
          case 'hours':
            this.$emit('input', modifyTime(this.value, value, this.minutes, this.seconds));
            break;
          case 'minutes':
            this.$emit('input', modifyTime(this.value, this.hours, value, this.seconds));
            break;
          case 'seconds':
            this.$emit('input', modifyTime(this.value, this.hours, this.minutes, value));
            break;
        }
      }
    },
    watch: {
      focusIndex(newVal, oldVal) {
        const el = this.$refs[this.focusRefs[newVal]];
        this.focusEl(el);
        const oldEl = this.$refs[this.focusRefs[oldVal]];
        oldEl.$el.setAttribute('tabindex', -1);
      }
    },
    mounted() {
      this.$refs[this.focusRefs[this.focusIndex]].$el.setAttribute('tabindex', 0);
    }
  };
</script>

<style lang="scss">
  @import "../../../scss/mixins/mixins";
  @import "../../../scss/common/var";

  @include b(time-input) {
    display: flex;
    align-items: center;
    line-height: 30px;
    height: $--input-height;
    -webkit-appearance: none;
    background-color: $--input-fill;
    background-image: none;
    border-radius: $--input-border-radius;
    border: 1px solid $--input-border-color;
    box-sizing: border-box;
    color: $--input-color;
    font-size: inherit;
    outline: none;
    padding: 0 4px;
    transition: $--border-transition-base;

    &.inner-focus {
      outline: none;
      border-color: $--input-focus-border;
    }

    @include e(sep) {
      position: relative;
      top: -1px;
    }
    @include m(large){
      height: $--input-lg-height;;
      line-height: $--input-lg-height - 2;
    }
  }
</style>
